import { FILE_TYPE } from '@/utils/constant'
import { FILE_PREVIEW_OFFICE, resolveFilePreviewType, resolveFileType } from '@/utils/download'

const PREVIEWABLE_FILE_TYPES = new Set([
  FILE_PREVIEW_OFFICE,
  FILE_TYPE.PDF,
  FILE_TYPE.IMAGE,
  FILE_TYPE.VIDEO,
  FILE_TYPE.AUDIO,
  FILE_TYPE.TXT,
])

const firstResourceUrl = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim())?.trim() || ''

/**
 * @description 规范化内容详情中的单个资源记录，兼容主资源与附件的字段差异。
 * @param {Object} source - 原始资源记录
 * @param {Object} [fallback] - 内容详情的兜底字段
 * @returns {{url: string, fileName: string, fileType: string, fileSize: number|string, poster: string}}
 */
const normalizeResourceRecord = (source = {}, fallback = {}) => {
  const record = source && typeof source === 'object' ? source : {}
  const fallbackRecord = fallback && typeof fallback === 'object' ? fallback : {}
  const url = firstResourceUrl(
    record.fileUrl,
    record.previewUrl,
    record.url,
    record.downloadUrl,
    fallbackRecord.fileUrl,
    fallbackRecord.previewUrl,
    fallbackRecord.url,
    fallbackRecord.downloadUrl
  )

  return {
    url: String(url || '').trim(),
    fileName: String(
      record.fileName || record.name || fallbackRecord.fileName || fallbackRecord.name || ''
    ).trim(),
    fileType: String(record.fileType || record.type || fallbackRecord.fileType || '').trim(),
    fileSize:
      record.fileSize ?? record.size ?? fallbackRecord.fileSize ?? fallbackRecord.size ?? '',
    poster: String(
      firstResourceUrl(
        record.coverUrl,
        record.videoCover?.previewUrl,
        record.videoCover?.url,
        fallbackRecord.coverUrl,
        fallbackRecord.videoCover?.previewUrl,
        fallbackRecord.videoCover?.url
      )
    ).trim(),
  }
}

/**
 * @description 解析内容详情的主展示资源，优先主文件，主文件缺失时回退至首个有效附件。
 * @param {Object} content - 内容详情接口返回的数据
 * @returns {{
 *   url: string,
 *   fileName: string,
 *   fileType: string,
 *   fileSize: number|string,
 *   poster: string,
 *   contentForm: string,
 *   contentId: string|number,
 *   previewType: string,
 *   hasResource: boolean,
 *   isPreviewable: boolean
 * }} 详情主资源及其展示能力
 */
export const resolveContentDetailResource = (content = {}) => {
  const detail = content && typeof content === 'object' ? content : {}
  const primaryResource = normalizeResourceRecord(detail.primaryResource, detail)
  const attachmentList = detail.attachmentInfoList || detail.attachments || detail.files || []
  const attachmentResource = Array.isArray(attachmentList)
    ? attachmentList
        .map((attachment) => normalizeResourceRecord(attachment))
        .find((attachment) => attachment.url)
    : null
  const resource = primaryResource.url ? primaryResource : attachmentResource || primaryResource
  const contentForm = String(detail.contentForm || '')
    .trim()
    .toUpperCase()
  const fileType = resolveFileType({
    contentForm,
    fileName: resource.fileName,
    fileType: resource.fileType,
    fileUrl: resource.url,
  })
  const previewType = resolveFilePreviewType({
    url: resource.url,
    fileName: resource.fileName,
    fileType,
  })

  return {
    ...resource,
    contentForm,
    contentId: detail.contentId || detail.id || '',
    downloadUrl: resource.url,
    fileType,
    hasResource: Boolean(resource.url),
    isPreviewable: PREVIEWABLE_FILE_TYPES.has(previewType),
    previewType,
  }
}
